import React, { useState, useEffect } from "react";
import { Navigation2, CheckCircle, Loader2, X } from "lucide-react";
import {
  Button,
  Alert,
  Card,
  CardContent,
  CardFooter,
  Select,
} from "../components/ui";
import {
  getStatusColor,
  formatDate,
  getStatusText,
  openDirections,
} from "../utils/utils";
import {
  getOrders,
  markAsComplete,
  getRacksForOrder,
  updateNotAddressedReason,
} from "../services/orders";
import { useNavigate } from "react-router-dom";
import orderStore from "../stores/OrderStore";
import { observer } from "mobx-react";
import { NOT_ADDRESSED_REASONS } from "../constants/notAddressedReasons";

export const OrdersScreen = observer(() => {
  const [loading, setLoading] = useState(false);
  const [showRacksModal, setShowRacksModal] = useState(false);
  const [racksLoading, setRacksLoading] = useState(false);
  const [groupedCells, setGroupedCells] = useState([]);
  const [groupedOversizeClothes, setGroupedOversizeClothes] = useState([]);
  const [selectedReasons, setSelectedReasons] = useState({});
  const [reasonLoading, setReasonLoading] = useState({});
  const [reasonMessages, setReasonMessages] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "RIDER_LOCATION") {
          // skip for now
        }
      } catch (e) {
        // Ignore errors
      }
    };

    document.addEventListener("message", handler);
    window.addEventListener("message", handler);

    return () => {
      document.removeEventListener("message", handler);
      window.removeEventListener("message", handler);
    };
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    getOrders()
      .then((data) => {
        orderStore.setOrders(data.items);
      })
      .then((err) => {});
    setLoading(false);
  };

  const handleGetRacks = async (orderId) => {
    setRacksLoading(true);
    setShowRacksModal(true);

    try {
      const data = await getRacksForOrder(orderId);

      // Group cells by cell_code and count entries
      const grouped = (data.packets || []).reduce((acc, cell) => {
        const cellCode = cell.cell_code;
        if (acc[cellCode]) {
          acc[cellCode].count += 1;
        } else {
          acc[cellCode] = { cell_code: cellCode, count: 1 };
        }
        return acc;
      }, {});

      // Group oversize clothes by display name and sum quantity
      const groupedOversize = (data.oversize_clothes || []).reduce(
        (acc, item) => {
          const clothName =
            item.display_name || item.cloth_name || item.key || "Unknown";
          const quantity = Number(item.quantity);
          const count = Number.isFinite(quantity) ? quantity : 1;

          if (acc[clothName]) {
            acc[clothName].count += count;
          } else {
            acc[clothName] = { cloth_name: clothName, count };
          }

          return acc;
        },
        {},
      );

      setGroupedCells(Object.values(grouped));
      setGroupedOversizeClothes(Object.values(groupedOversize));
    } catch (error) {
      console.error("Error fetching packet cells:", error);
      setGroupedCells([]);
      setGroupedOversizeClothes([]);
    } finally {
      setRacksLoading(false);
    }
  };

  const closeRacksModal = () => {
    setShowRacksModal(false);
    setGroupedCells([]);
    setGroupedOversizeClothes([]);
  };

  const handleReasonChange = async (
    orderId,
    reason,
    preferredDateTime = null,
  ) => {
    if (!reason) return;

    setReasonLoading((prev) => ({ ...prev, [orderId]: true }));
    setReasonMessages((prev) => ({ ...prev, [orderId]: null }));

    try {
      await updateNotAddressedReason(orderId, reason, preferredDateTime);
      setReasonMessages((prev) => ({
        ...prev,
        [orderId]: { type: "success", message: "Reason updated successfully!" },
      }));
      setSelectedReasons((prev) => ({ ...prev, [orderId]: reason }));
    } catch (error) {
      console.error("Error updating reason:", error);
      setReasonMessages((prev) => ({
        ...prev,
        [orderId]: {
          type: "error",
          message: "Failed to update reason. Please try again.",
        },
      }));
    } finally {
      setReasonLoading((prev) => ({ ...prev, [orderId]: false }));

      // Clear message after 3 seconds
      setTimeout(() => {
        setReasonMessages((prev) => {
          const newMessages = { ...prev };
          delete newMessages[orderId];
          return newMessages;
        });
      }, 3000);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  return (
    <div className="p-4 space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Orders</h1>
        <Button
          onClick={() => {
            fetchOrders();
          }}
        >
          Refresh
        </Button>
        <Button
          className="mr-3"
          onClick={() => {
            navigate("/customers");
          }}
        >
          Customers
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center items-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      )}

      {!loading && orderStore.orders.length === 0 && (
        <Alert>No orders found</Alert>
      )}

      {orderStore.orders.map((order) => (
        <Card key={order.id} className="bg-white shadow-md">
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div className="space-y-2">
                <span
                  className={`inline-block px-2 py-1 rounded text-sm ${getStatusColor(
                    order.status,
                  )}`}
                >
                  {getStatusText(order.status)}
                </span>
                <h3 className="font-medium">{order.full_name}</h3>
                <p className="text-sm text-gray-600">Id: {order.id}</p>
                <p className="text-sm text-gray-600">{order.address_1}</p>
                <p className="text-sm text-gray-600">{order.address_2}</p>
                {order.landmark && (
                  <p className="text-sm text-gray-500">
                    Landmark: {order.landmark}
                  </p>
                )}
                <p className="text-xs text-gray-400">
                  Created: {formatDate(order.created_at)}
                </p>
                <p>
                  <a
                    className="text-sm text-blue-500"
                    href={`tel:${order.phone_number}`}
                  >
                    {order.phone_number}
                  </a>
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openDirections(order.latitude, order.longitude)}
                className="mt-1"
              >
                <Navigation2 className="h-5 w-5" />
              </Button>
            </div>
          </CardContent>
          <CardFooter className="p-4 flex justify-between">
            <Button
              onClick={() => {
                navigate(`orders/${order.id}`);
              }}
            >
              View Details
            </Button>
            {order.status === 4 && (
              <Button onClick={() => handleGetRacks(order.id)}>
                Get Racks
              </Button>
            )}
            {order.membership ? (
              <></>
            ) : (
              <div className="bg-red-500 text-white px-3 py-1 rounded-full text-lg font-medium">
                No Gold
              </div>
            )}
            {order.status === 0 && (
              <Button
                variant="outline"
                onClick={() => markAsComplete(order.id)}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Mark Complete
              </Button>
            )}
          </CardFooter>

          {/* Not Addressed Reason Dropdown */}
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="pt-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Not Addressed Reason:
              </label>
              <Select
                options={NOT_ADDRESSED_REASONS}
                value={selectedReasons[order.id] || ""}
                onChange={(reason, preferredDateTime) =>
                  handleReasonChange(order.id, reason, preferredDateTime)
                }
                placeholder="Select reason..."
                allowCustomInput={true}
                customInputPlaceholder="Enter custom reason..."
                className="w-full"
              />

              {/* Loading indicator */}
              {reasonLoading[order.id] && (
                <div className="flex items-center mt-2 text-sm text-blue-600">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating reason...
                </div>
              )}

              {/* Success/Error message */}
              {reasonMessages[order.id] && (
                <div
                  className={`mt-2 text-sm ${
                    reasonMessages[order.id].type === "success"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {reasonMessages[order.id].message}
                </div>
              )}
            </div>
          </div>
        </Card>
      ))}

      {/* Packet Cells Modal */}
      {showRacksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Packet Cells</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeRacksModal}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {racksLoading ? (
              <div className="flex justify-center items-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="ml-2">Loading packet cells...</span>
              </div>
            ) : groupedCells.length === 0 ? (
              <Alert>No packet cells found for this order.</Alert>
            ) : (
              <div className="space-y-3">
                {groupedCells.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Packet Cells
                    </h3>
                    <div className="grid grid-cols-2 gap-4 font-medium text-gray-500 text-sm border-b pb-2">
                      <div>Cell Code</div>
                      <div>Count</div>
                    </div>
                    {groupedCells.map((group, index) => (
                      <div
                        key={`cell-${index}`}
                        className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100"
                      >
                        <div className="font-semibold text-lg">
                          {group.cell_code}
                        </div>
                        <div className="font-semibold text-lg text-blue-600">
                          {group.count}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {groupedOversizeClothes.length > 0 && (
                  <div className="space-y-3 pt-2">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Oversize Clothes
                    </h3>
                    <div className="grid grid-cols-2 gap-4 font-medium text-gray-500 text-sm border-b pb-2">
                      <div>Cloth Name</div>
                      <div>Count</div>
                    </div>
                    {groupedOversizeClothes.map((group, index) => (
                      <div
                        key={`oversize-${index}`}
                        className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100"
                      >
                        <div className="font-semibold text-lg">
                          {group.cloth_name}
                        </div>
                        <div className="font-semibold text-lg text-blue-600">
                          {group.count}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button onClick={closeRacksModal}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
